/**
 * Vue d'ensemble des diagnostics de calcul — administrateurs de la plateforme.
 *
 * Un diagnostic isolé dit ce qu'un calcul a fait de son temps ; la page de la
 * simulation le montre déjà. Cette vue-ci répond à l'autre question : quelles
 * pistes reviennent, et sur combien de cas. Une piste vue une fois est une
 * particularité, la même sur trois cas est un défaut de plateforme qui se
 * corrige une fois pour tous.
 *
 * L'ordre d'affichage porte cette idée : les récurrences d'abord, le détail
 * par calcul ensuite. Le tri vient du serveur, la page ne réinterprète rien.
 *
 * Le privilège est appliqué côté serveur (403) et par SuperadminRoute. Ce
 * fichier ne garde rien : il affiche ce qu'on lui a donné le droit de lire.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import simulationService from '../../services/simulationService';

const TONS = {
  fort: 'bg-error-500/10 border-error-500/30',
  moyen: 'bg-warning-500/10 border-warning-500/30',
  faible: 'bg-dark-800 border-dark-700',
};

const Chiffre = ({ libelle, valeur, unite }) => (
  <div className="bg-dark-900 rounded-lg p-3">
    <p className="text-dark-500 text-xs uppercase tracking-wider mb-1">{libelle}</p>
    <p className="text-dark-100 font-mono">
      {valeur === null || valeur === undefined ? '—' : valeur}
      {valeur !== null && valeur !== undefined && unite && (
        <span className="text-dark-500 text-sm ml-1">{unite}</span>
      )}
    </p>
  </div>
);

const DiagnosticsAdminPage = () => {
  const { t } = useTranslation();
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [deplie, setDeplie] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      setDonnees(await simulationService.getDiagnostics(50));
    } catch (err) {
      setErreur(
        err.response?.status === 403
          ? t('diagnosticsAdmin.refuse')
          : t('diagnosticsAdmin.echec')
      );
    } finally {
      setChargement(false);
    }
  }, [t]);

  useEffect(() => {
    charger();
  }, [charger]);

  const recurrences = donnees?.recurrences || [];
  const diagnostics = donnees?.diagnostics || [];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">{t('diagnosticsAdmin.titre')}</h1>
          <p className="text-dark-400 text-sm mt-1">{t('diagnosticsAdmin.intro')}</p>
        </div>
        <button
          onClick={charger}
          disabled={chargement}
          className="shrink-0 px-4 py-2 text-sm rounded-lg bg-dark-800 border border-dark-700 text-dark-200 hover:bg-dark-700 disabled:opacity-50"
        >
          {chargement ? t('diagnosticsAdmin.chargement') : t('diagnosticsAdmin.rafraichir')}
        </button>
      </div>

      {erreur && (
        <div className="bg-error-500/10 border border-error-500/30 rounded-lg p-4">
          <p className="text-error-400 text-sm">{erreur}</p>
        </div>
      )}

      {!erreur && !chargement && diagnostics.length === 0 && (
        <div className="bg-dark-800 border border-dark-700 rounded-lg p-6">
          <p className="text-dark-300 text-sm">{t('diagnosticsAdmin.aucun')}</p>
        </div>
      )}

      {diagnostics.length > 0 && (
        <>
          <section>
            <h2 className="text-dark-100 font-semibold mb-1">
              {t('diagnosticsAdmin.recurrences')} ({recurrences.length})
            </h2>
            <p className="text-dark-500 text-xs mb-3">{t('diagnosticsAdmin.recurrencesAide')}</p>
            <div className="space-y-3">
              {recurrences.map((r, i) => (
                <div key={i} className={`border rounded-lg p-4 ${TONS[r.niveau] || TONS.faible}`}>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-xs uppercase tracking-wider text-dark-400 font-medium">
                      {t(`diagnostic.domaine.${r.domaine}`, r.domaine)}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded bg-dark-900 text-dark-300 font-mono">
                      {t('diagnosticsAdmin.casTouches', { count: r.cases.length })}
                    </span>
                  </div>
                  {r.constat && (
                    <p className="text-dark-100 text-sm font-medium mb-1">{r.constat}</p>
                  )}
                  <p className="text-dark-200 text-sm">{r.piste}</p>
                  <p className="text-dark-500 text-xs mt-2 font-mono">{r.cases.join(' · ')}</p>
                  <p className="text-dark-600 text-xs mt-1">
                    {r.occurrences > 1
                      ? t('diagnosticsAdmin.occurrences_autres', { count: r.occurrences })
                      : t('diagnosticsAdmin.occurrences_une')}
                    {r.derniere_occurrence
                      ? ` · ${t('diagnosticsAdmin.vuLe')} ${new Date(r.derniere_occurrence).toLocaleString()}`
                      : ''}
                  </p>
                  {(r.exemples || []).length > 0 && (
                    <p className="text-xs mt-1 font-mono space-x-2">
                      {r.exemples.map((sim) => (
                        <Link
                          key={sim}
                          to={`/app/simulations/${sim}`}
                          className="text-primary-400 hover:text-primary-300"
                        >
                          {sim}
                        </Link>
                      ))}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-dark-100 font-semibold mb-3">
              {t('diagnosticsAdmin.parCalcul')} ({diagnostics.length}
              {donnees.total > diagnostics.length ? ` / ${donnees.total}` : ''})
            </h2>
            <div className="space-y-2">
              {diagnostics.map((d) => {
                const m = d.mesures || {};
                const ouvert = deplie === d.simulation_id;
                return (
                  <div key={d.simulation_id} className="bg-dark-800 border border-dark-700 rounded-lg">
                    <button
                      onClick={() => setDeplie(ouvert ? null : d.simulation_id)}
                      className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-left hover:bg-dark-700/50 rounded-lg"
                    >
                      <span className="text-dark-100 text-sm font-medium">{d.case}</span>
                      <span className="text-dark-500 text-xs font-mono">{d.simulation_id}</span>
                      {d.horodatage && (
                        <span className="text-dark-600 text-xs">
                          {new Date(d.horodatage).toLocaleString()}
                        </span>
                      )}
                      <span className="text-dark-400 text-xs ml-auto">
                        {m.statut} · {m.duree_totale_min} min · {(d.pistes || []).length}{' '}
                        {t('diagnosticsAdmin.pistesCourt')}
                      </span>
                    </button>
                    {ouvert && (
                      <div className="px-4 pb-4 space-y-4">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                          <Chiffre libelle={t('diagnostic.iterations')} valeur={m.iterations_conception} />
                          <Chiffre libelle={t('diagnostic.cadence')} valeur={m.duree_iteration_conception_min} unite="min" />
                          <Chiffre libelle={t('diagnostic.partAdjoint')} valeur={m.part_adjoint_pourcent} unite="%" />
                          <Chiffre libelle={t('diagnostic.picMemoire')} valeur={m.memoire_pic_mib} unite="Mio" />
                          <Chiffre libelle={t('diagnostic.pourcentReservation')} valeur={m.memoire_pourcent_reservation} unite="%" />
                          <Chiffre libelle={t('diagnostic.pourcentMachine')} valeur={m.memoire_pourcent_machine} unite="%" />
                          <Chiffre libelle={t('diagnostic.pourcentCoeurs')} valeur={m.coeurs_pourcent} unite="%" />
                          <Chiffre libelle={t('diagnostic.rangs')} valeur={m.rangs_mpi} />
                          <Chiffre libelle={t('diagnostic.machine')} valeur={m.machine_type} />
                          <Chiffre libelle={t('diagnostic.machineCycleVie')} valeur={m.machine_cycle_vie} />
                        </div>
                        {(d.pistes || []).map((p, i) => (
                          <div key={i} className={`border rounded-lg p-3 ${TONS[p.niveau] || TONS.faible}`}>
                            <p className="text-xs uppercase tracking-wider text-dark-400 mb-1">
                              {t(`diagnostic.domaine.${p.domaine}`, p.domaine)}
                            </p>
                            <p className="text-dark-200 text-sm">{p.constat}</p>
                            <p className="text-dark-300 text-sm mt-1">
                              <span className="text-dark-500">{t('diagnostic.aFaire')}</span>
                              {p.piste}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default DiagnosticsAdminPage;
